import * as React from "react";

import { faArrowRight } from "@fortawesome/sharp-regular-svg-icons/faArrowRight";
import { faExternalLink } from "@fortawesome/sharp-regular-svg-icons/faExternalLink";
import clsx from "clsx";

import { getLinkProps } from "~utils/link_utils/getLinkProps";

import { Box } from "~components/Box";
import { IconBrand } from "~components/IconBrand";
import { IconFontAwesome } from "~components/IconFontAwesome";
import { Link } from "~components/Link";
import { Typography } from "~components/Typography";

import { type ListItem as ListItemInterface } from "~sections/Nav/Nav.types";
import { SubListItem } from "~sections/Nav/desktop_new/_SubListItem";
import * as styles from "~sections/Nav/desktop_new/styles.css";

interface ListItemProps extends ListItemInterface {
  className?: string;
  children?: React.ReactNode;
}

export const ListItem = React.forwardRef<HTMLAnchorElement, ListItemProps>(
  ({ className, children, title, icon, link, subListItems }, forwardedRef) => {
    const { linkType } = getLinkProps(link) || {};
    const isInternalLink = linkType === "internalLink";

    return (
      <Box width="100%">
        <Link
          className={clsx(styles.listItemLink, className)}
          link={link}
          ref={forwardedRef}
        >
          {icon && (
            <Box className={styles.listItemIconContainer}>
              <IconBrand
                className={styles.listItemIcon}
                width="spacing3"
                height="spacing3"
                icon={icon}
                aria-hidden
              />
            </Box>
          )}
          <Box display="flex" flexDirection="column">
            <Box display="flex" gap="spacing1" alignItems="center">
              <Typography
                className={styles.listItemHeading}
                fontWeight="bold"
                as="span"
              >
                {title}
              </Typography>
              <IconFontAwesome
                icon={faArrowRight}
                className={clsx(
                  isInternalLink
                    ? styles.listItemIconVisible
                    : styles.listItemIconHidden
                )}
              />
              <IconFontAwesome
                icon={faExternalLink}
                className={clsx(
                  isInternalLink
                    ? styles.listItemIconHidden
                    : styles.listItemIconVisible
                )}
              />
            </Box>
            <Typography
              textAppearance="body_sm"
              className={styles.listItemText}
              as="span"
            >
              {children}
            </Typography>
          </Box>
        </Link>
        <Box
          display="flex"
          gap="none"
          flexDirection="column"
          marginTop="spacing0"
        >
          {subListItems?.map((subListItem) => {
            return (
              <SubListItem
                key={subListItem.title}
                title={subListItem.title}
                link={subListItem.link}
              />
            );
          })}
        </Box>
      </Box>
    );
  }
);
